/* eslint-disable react/no-unescaped-entities */
/*
 * 📖 DFCL Rulebook — Dragonious Flame Card Legends Official Rules
 * ----------------------------------------------------------------
 * Owner canon, filed verbatim from Roc 2026-09-17 (plus 09-18 Accessory
 * drop and 09-19 Evolution Rule). Lives in the Arcade's CCG section as
 * a readable rules reference — players should be able to learn the
 * game from zero without ever leaving this page.
 *
 * SPELLING POLICY: Do NOT "fix" Roc's spellings. Quite Mend, 13
 * Constellation, etc. — same rule applies site-wide.
 *
 * Card-type frame colors are kept consistent with `CLASS_RIBBON` from
 * `DragoniousCCG.jsx`:
 *   hero=emerald · villain=red · storyline=indigo · skill=slate
 *   showtime=amber · fatality=red · harmony=purple · accessory=cyan (new)
 */

import { X, Printer, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

// 🎴 Owner-supplied card-back icon (Golden back + Dragonious Fighters
// color scheme — purple / red / black per /app/memory canon).
const CARD_BACK_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/" +
  "job_omni-brand-1/artifacts/b6q7tkhp_Approved%20card%20back.webp";

// Frame-color legend — matches the existing CCG renders. Accessory is
// the only new type (owner Roc 2026-09-18); cyan sets it apart from
// Finisher amber and Harmony purple without colliding with any existing
// ribbon.
const CARD_TYPE_LEGEND = [
  { id: "fighter",   label: "Fighter (Hero)",   dot: "bg-emerald-500",  desc: "Combat unit. 1 designated Starting Vanguard — no Fighter cap in a Main Deck." },
  { id: "villain",   label: "Fighter (Villain)",dot: "bg-red-800",      desc: "Combat unit, enemy-side. Same slot family as Heroes." },
  { id: "skill",     label: "Skill / Special",  dot: "bg-slate-500",    desc: "Equipped onto a Fighter in Phase 2 Main." },
  { id: "storyline", label: "Storyline",        dot: "bg-indigo-500",   desc: "Global field modifier. May carry a Climax Trigger (instant-win condition)." },
  { id: "harmony",   label: "Harmony",          dot: "bg-purple-500",   desc: "Activates in Phase 2 when character/elemental conditions align." },
  { id: "showtime",  label: "Showtime",         dot: "bg-amber-500",    desc: "Finisher Zone. ≤25% HP or Guard-Broken + full Gauge." },
  { id: "fatality",  label: "Fatality",         dot: "bg-red-500",      desc: "Finisher Zone. ≤15% HP. Removes from play — bypasses Graveyard." },
  { id: "accessory", label: "Accessory",        dot: "bg-cyan-400",     desc: "Cosmic-item type. Sits with the hero(es); enables Special Summons." },
];

export default function DFCLRulebook({ onClose, standalone = false }) {
  // 📄 Browser-native print pipeline — the `@media print` CSS below
  // strips the overlay chrome and prints just the rulebook page.
  // Users can "Save as PDF" from the print dialog on any OS, which
  // gives us a tournament-ready reference without a PDF library.
  const handlePrint = () => {
    try {
      window.print();
    } catch (_e) {
      toast.error("Printing failed — try your browser's File → Print menu.");
    }
  };

  // 🔗 Shareable deep link to the standalone rules page.
  const handleCopyLink = async () => {
    const url = `${window.location.origin}/arcade/rules/dfcl`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Rulebook link copied — share it anywhere.");
    } catch (_e) {
      toast.error(`Couldn't auto-copy. The URL is: ${url}`);
    }
  };

  // In standalone mode (on /arcade/rules/dfcl) we don't dim the page or
  // trap clicks — the rulebook IS the page. The overlay chrome stays
  // identical in both modes so the print output is consistent.
  const containerCls = standalone
    ? "min-h-screen bg-black text-white"
    : "fixed inset-0 z-[80] bg-black/90 backdrop-blur-md overflow-y-auto";

  return (
    <div
      className={`${containerCls} dfcl-rulebook-root`}
      data-testid="dfcl-rulebook-overlay"
      onClick={standalone ? undefined : onClose}
    >
      <div
        className="min-h-screen max-w-4xl mx-auto px-4 sm:px-6 py-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Controls — Print + Copy Link + Close. The `dfcl-no-print`
            class strips them from the printed/saved-as-PDF output. */}
        <div className="flex justify-end items-center gap-2 mb-4 dfcl-no-print">
          <button
            onClick={handleCopyLink}
            data-testid="dfcl-rulebook-copy-link"
            title="Copy shareable link"
            className="text-neutral-400 hover:text-amber-200 border border-zinc-700 hover:border-amber-400 h-10 px-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest transition-colors"
          >
            <LinkIcon size={14} />
            <span className="hidden sm:inline">Copy Link</span>
          </button>
          <button
            onClick={handlePrint}
            data-testid="dfcl-rulebook-print"
            title="Print or save as PDF"
            className="text-neutral-400 hover:text-amber-200 border border-zinc-700 hover:border-amber-400 h-10 px-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest transition-colors"
          >
            <Printer size={14} />
            <span className="hidden sm:inline">Export PDF</span>
          </button>
          {!standalone && (
            <button
              onClick={onClose}
              data-testid="dfcl-rulebook-close"
              className="text-neutral-400 hover:text-white border border-zinc-700 hover:border-fuchsia-400 w-10 h-10 flex items-center justify-center transition-colors"
              aria-label="Close rulebook"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* ─── Hero header ────────────────────────────────────── */}
        <div className="border border-amber-500/40 bg-gradient-to-br from-[#1a0808] via-[#120410] to-black p-6 sm:p-8 mb-6">
          <div className="flex items-start gap-5 flex-wrap">
            <img
              src={CARD_BACK_URL}
              alt="Dragonious Flame Card Legends — card back"
              className="w-28 sm:w-36 border border-amber-500/60 shadow-[0_0_30px_rgba(217,119,6,0.4)]"
              draggable={false}
              data-testid="dfcl-rulebook-cardback"
            />
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">
                🔥 Dragonious Flame Card Legends
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-white mt-1 leading-none">
                Official Rules
              </h1>
              <p className="text-sm text-neutral-400 mt-3 leading-snug">
                A 2-player strategic CCG that simulates high-intensity combat
                from <em>Dragonious Fighters</em> and the lore of <em>Phoenix Flame</em>.
                Construct a deck to field Fighters, trigger dynamic Storylines,
                and execute match-ending Finishers.
              </p>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-neutral-500">
                Source: Roc, 2026-09-17 (+ 09-18 Accessory drop, + 09-19 Evolution Rule) · Hard canon
              </div>
            </div>
          </div>
        </div>

        {/* ─── Deck Construction ────────────────────────────── */}
        <Section num="01" title="Deck Construction">
          <StatRow label="Main Deck size"       value="Exactly 50 cards" />
          <StatRow label="Finisher Side-Deck"   value="5–10 cards (Showtime & Fatality)" />
          <StatRow label="Card copy limit"      value="Max 3 copies by title" subnote="1 copy per Divine/God card" />
          <StatRow label="Fighters"             value="At least 1 Fighter, no cap" subnote="1 designated as Starting Vanguard" />
          <StatRow label="Starting hand"        value="5 cards" />
          <StatRow label="Max hand"             value="7 cards (end-of-turn cap)" />
        </Section>

        {/* ─── Card Types ────────────────────────────────────── */}
        <Section num="02" title="Card Types">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
            {CARD_TYPE_LEGEND.map((t) => (
              <div
                key={t.id}
                data-testid={`card-type-${t.id}`}
                className="border border-zinc-800 bg-black/40 p-3 flex items-start gap-3"
              >
                <span className={`${t.dot} w-3 h-3 mt-1.5 rounded-full flex-shrink-0`} />
                <div className="flex-1">
                  <div className="text-white text-sm font-semibold">{t.label}</div>
                  <div className="text-neutral-400 text-[11px] leading-snug mt-0.5">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <SubHead>Accessory — Cosmic-item card type (Roc 2026-09-18)</SubHead>
          <ul className="text-sm text-neutral-300 leading-relaxed space-y-2 pl-5 list-disc marker:text-cyan-500">
            <li>Not magic (Spell), not a unit (Fighter), not a Summon — but it does summon. It enables Special Summons.</li>
            <li>Sits in battle as an accessory — <em>with</em> the hero(es), not necessarily wielded by them. The item itself enables the big plays.</li>
            <li>First Accessory: <span className="text-cyan-300">#160 Wifi-Connection</span> (Brice's accessory).</li>
            <li><span className="text-amber-200">#159 Deanah &amp; Muki Cosmic Ultimate Weapon</span> functions AS an accessory in battle — with them, not necessarily her — but its type is <strong>Cosmic</strong>, not Accessory.</li>
            <li>Most Cosmic items should work like this on some level.</li>
          </ul>

          <SubHead extra="mt-6">Evolution Rule (Roc 2026-09-19)</SubHead>
          <p className="text-sm text-neutral-300 leading-relaxed mb-3">
            If a hero's Normal form is on the battlefield <strong>with</strong> its
            Cosmic Ultimate Weapon:
          </p>
          <ol className="text-sm text-neutral-300 leading-relaxed space-y-2 pl-5 list-decimal marker:text-amber-400">
            <li>Send the Normal form to the <strong>Ash Pile</strong>.</li>
            <li>The Cosmic Ultimate Weapon <strong>summons</strong> the Evolution form from the deck to the battlefield.</li>
            <li>Cost: <strong className="text-amber-300">Pay 5 tokens</strong> (half the 10-token cap — standard summon cost).</li>
          </ol>
          <div className="mt-3 border-l-2 border-amber-500/40 pl-3 text-[12px] text-neutral-400 italic leading-snug">
            Example: Normal David Lauer + his Cosmic Ultimate Weapon on the field → send Normal David Lauer to the Ash Pile → weapon summons Evolution David Lauer. (David Lauer has no canon Evolution yet; the rule stands regardless.)
          </div>
          <div className="mt-3 text-[12px] text-red-300/90 font-semibold">
            ⚠️ Weapons only — Cosmic Ultimate <em>Armors</em> do NOT trigger the Evolution Rule.
          </div>
        </Section>

        {/* ─── Table Layout & Zones ─────────────────────────── */}
        <Section num="03" title="Table Layout & Zones">
          <ZoneRow name="Active Fighter Zone"           desc="The primary slot where combat takes place." />
          <ZoneRow name="Bench / Support Zone"          desc="Reserve Fighters — passives, assist attacks, swap into combat." tag="Max 3" />
          <ZoneRow name="Field / Storyline Zone"        desc="Active location for global narrative cards." />
          <ZoneRow name="Command Deck & Discard (Graveyard)" desc="Draw pile and discarded actions." />
          <ZoneRow name="Finisher Zone"                 desc="Extra deck — Showtime, Fatality, Stance Transformations." />
          <ZoneRow name="Finisher Gauge / Meter Track"  desc="Tracker used to build toward Showtime and Fatality finishers." />
        </Section>

        {/* ─── Core Mechanics ───────────────────────────────── */}
        <Section num="04" title="Core Game Mechanics">
          <SubHead>1 · Token Resource System</SubHead>
          <ul className="text-sm text-neutral-300 leading-relaxed space-y-1.5 pl-5 list-disc marker:text-amber-400">
            <li>Turn 1 starts at <strong>1 Token</strong>. <strong>+1 Max Tokens</strong> at the start of every turn.</li>
            <li>Token cap: <strong className="text-amber-300">10</strong>.</li>
            <li>Tokens fully refill at the start of your turn.</li>
            <li>Spent to play Fighters, Skills, Specials, and Storyline cards.</li>
          </ul>

          <SubHead extra="mt-5">2 · Guarding &amp; Damage Dampening</SubHead>
          <p className="text-sm text-neutral-300 leading-relaxed mb-2">
            When targeted by an attack or Special card, the defender may spend <strong>1 Token</strong> to declare a <strong>Block</strong>.
          </p>
          <ul className="text-sm text-neutral-300 leading-relaxed space-y-1.5 pl-5 list-disc marker:text-emerald-400">
            <li><strong>Block Effect:</strong> cuts incoming damage by <strong>50%</strong> + forces attacker's crit chance to <strong>0%</strong> for that strike.</li>
            <li><strong>Guard Break:</strong> attacks like <em>Blaze Breaker</em> apply <strong className="text-red-400">Guard-Broken</strong> status — the target cannot Block and takes <strong>+2</strong> extra damage from subsequent attacks.</li>
          </ul>

          <SubHead extra="mt-5">3 · Execution Thresholds &amp; Finishers</SubHead>
          <ul className="text-sm text-neutral-300 leading-relaxed space-y-1.5 pl-5 list-disc marker:text-amber-400">
            <li><span className="text-amber-300 font-semibold">Showtime</span> — opponent's Active Fighter at <strong>≤ 25% HP</strong> OR Guard-Broken, AND your Finisher Gauge is <strong>FULL</strong>.</li>
            <li><span className="text-red-400 font-semibold">Fatality</span> — opponent's Fighter at <strong>≤ 15% HP</strong> (Critical Threshold). Instantly executes the unit, <strong>removing it from play entirely — bypasses Graveyard</strong> (no revives).</li>
          </ul>
        </Section>

        {/* ─── Turn Structure ───────────────────────────────── */}
        <Section num="05" title="Turn Structure — 5 Phases">
          <PhaseBlock n={1} title="Draw & Charge" tint="amber">
            <li>Draw 1 card (Draw 2 on Turn 1 if going second).</li>
            <li>Max Tokens +1, fully refill spent Tokens (cap 10).</li>
            <li>+1 Finisher Point on your gauge.</li>
          </PhaseBlock>
          <PhaseBlock n={2} title="Main" tint="indigo">
            <li>Play or equip Skill/Special cards onto your Active Fighter.</li>
            <li>Swap Active ↔ Bench Fighter — costs <strong>1 Token</strong>.</li>
            <li>Play a Storyline Card to override the current field modifier.</li>
            <li>Activate Harmony Cards when character/elemental conditions align.</li>
          </PhaseBlock>
          <PhaseBlock n={3} title="Combat" tint="red">
            <li><strong>Declare Attack</strong> — basic ATK, or pay Tokens for an equipped Special.</li>
            <li><strong>Defender Window</strong> — take full damage, play a Counter/Reaction Skill, or spend 1 Token to Block.</li>
            <li><strong>Damage</strong> = ATK − target DEF (modified by Block status). Deduct from target HP.</li>
          </PhaseBlock>
          <PhaseBlock n={4} title="Showtime / Execution" tint="amber">
            <li>If combat drops the Active Fighter below a threshold (25% Showtime / 15% Fatality) AND your Finisher Gauge is full, spend the gauge to play a Finisher immediately from your Finisher Zone.</li>
          </PhaseBlock>
          <PhaseBlock n={5} title="End" tint="purple">
            <li>Hand size check — max <strong>7</strong>, discard excess.</li>
            <li>Resolve "End of Turn" passives, burn damage, and Storyline effects.</li>
            <li>Turn passes.</li>
          </PhaseBlock>
        </Section>

        {/* ─── Life Points ─────────────────────────────────── */}
        <Section num="06" title="Life Points">
          <StatRow label="Starting Life Points"  value="10,000"  subnote="agreed, Roc 2026-09-17" />
          <StatRow label="Showtime trigger"      value="≤ 2,500 LP (25%)" subnote="comeback window" />
          <StatRow label="Fatality trigger"      value="≤ 1,500 LP (15%)" subnote="unblockable finishers" />
          <StatRow label="Blood deck tempo"      value="800–1,000 LP cost" subnote="safe vs Flame burn" />
          <p className="text-sm text-neutral-300 leading-relaxed mt-4">
            Direct-hit cards deal damage straight to Life Points, bypassing Fighters.
            Reducing the opponent to <strong className="text-red-400">0 LP</strong> wins the match.
          </p>
          <div className="mt-3 border-l-2 border-amber-500/40 pl-3 text-[12px] text-neutral-400 italic leading-snug">
            Why 10,000 — scaled for the Cosmic God tier. Some Cosmic God cards hit for 5,000 in a single shot (half your life). They're rare and hard to get, so it's not really a worry for now.
          </div>
        </Section>

        {/* ─── Standard Duel Setup (forwarded) ──────────────── */}
        <Section num="07" title="Standard 1v1 Duel Setup">
          <StatRow label="Starting Nexus HP"     value="10,000" subnote="Nexus HP = Life Points" />
          <StatRow label="God tier alignment"    value="Infernus · Sanguinis · Titanus" subnote="clean 1,000-point stat alignment" />
          <StatRow label="Starting hand"         value="5 cards" />
          <StatRow label="Max hand"              value="7 cards (end-of-turn cap)" />
          <StatRow label="Divine / God copy cap" value="1 per deck" />
          <div className="mt-4 border border-red-500/40 bg-red-950/20 p-3 text-[12px] leading-snug">
            <div className="text-red-400 font-bold uppercase tracking-widest text-[10px] mb-1">⚠️ Conflict resolved (Roc 2026-09-17)</div>
            An older note listed deck size as 40. Going backward through notes — <strong className="text-white">50 is current and canon.</strong> The 1-copy limit on Divine/God cards stands.
          </div>
        </Section>

        {/* ─── Victory ──────────────────────────────────────── */}
        <Section num="08" title="Victory Conditions">
          <p className="text-sm text-neutral-400 leading-relaxed mb-4">
            A player wins immediately by achieving any of the following:
          </p>
          <WinRow icon="💔" title="Life Points"       desc="Reduce the opponent to 0 LP (from 10,000 starting)." />
          <WinRow icon="⚔️" title="Total Defeat"       desc="Eliminate all of the opponent's Fighters (Active and Bench) via damage, Showtime, or Fatality." />
          <WinRow icon="🃏" title="Deck Out"           desc="Opponent is required to draw a card from their Main Deck but has none remaining." />
          <WinRow icon="📖" title="Narrative Climax"   desc="Successfully activate a Storyline Card whose specific Climax Trigger condition explicitly awards an instant match victory." />
        </Section>

        {/* ─── Footer ───────────────────────────────────────── */}
        <div className="text-center py-6 text-[11px] text-neutral-600 uppercase tracking-widest">
          Dragonious Flame Card Legends — Rulebook v1 · Canon law · Last update 2026-09-19
        </div>
      </div>
    </div>
  );
}

/* ─── Shared presentational atoms ──────────────────────────── */
function Section({ num, title, children }) {
  return (
    <section
      data-testid={`dfcl-section-${num}`}
      className="border border-zinc-800 bg-black/40 mb-5"
    >
      <div className="px-5 py-3 border-b border-zinc-900 flex items-baseline gap-3">
        <span className="font-mono tabular-nums text-[11px] text-amber-500">§{num}</span>
        <h2 className="font-display text-xl text-white leading-tight">{title}</h2>
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function SubHead({ children, extra = "" }) {
  return (
    <h3 className={`text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold mb-2 ${extra}`}>
      {children}
    </h3>
  );
}

function StatRow({ label, value, subnote }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-zinc-900 last:border-b-0">
      <div className="text-sm text-neutral-300">{label}</div>
      <div className="text-right">
        <div className="font-mono text-sm text-amber-200">{value}</div>
        {subnote && <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{subnote}</div>}
      </div>
    </div>
  );
}

function ZoneRow({ name, desc, tag }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-zinc-900 last:border-b-0">
      <div className="flex-1">
        <div className="text-white text-sm font-semibold flex items-center gap-2 flex-wrap">
          {name}
          {tag && <span className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/40 px-1.5 py-0.5">{tag}</span>}
        </div>
        <div className="text-[12px] text-neutral-400 leading-snug mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

function PhaseBlock({ n, title, tint, children }) {
  const tints = {
    amber:  { border: "border-amber-500/40",  text: "text-amber-300",  marker: "marker:text-amber-400" },
    indigo: { border: "border-indigo-500/40", text: "text-indigo-300", marker: "marker:text-indigo-400" },
    red:    { border: "border-red-500/40",    text: "text-red-300",    marker: "marker:text-red-400" },
    purple: { border: "border-purple-500/40", text: "text-purple-300", marker: "marker:text-purple-400" },
  }[tint] || { border: "border-zinc-700", text: "text-white", marker: "marker:text-neutral-400" };
  return (
    <div className={`border-l-2 ${tints.border} pl-4 mb-4 last:mb-0`} data-testid={`dfcl-phase-${n}`}>
      <div className={`text-[10px] uppercase tracking-[0.3em] font-bold ${tints.text}`}>
        Phase {n}
      </div>
      <div className="font-display text-lg text-white leading-tight mb-1.5">{title}</div>
      <ul className={`text-sm text-neutral-300 leading-relaxed space-y-1.5 pl-5 list-disc ${tints.marker}`}>
        {children}
      </ul>
    </div>
  );
}

function WinRow({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-zinc-900 last:border-b-0">
      <span className="text-2xl leading-none">{icon}</span>
      <div className="flex-1">
        <div className="text-white text-sm font-semibold">{title}</div>
        <div className="text-[12px] text-neutral-400 leading-snug mt-0.5">{desc}</div>
      </div>
    </div>
  );
}
